import { useMemo, useEffect } from "react";
import {
  CHATS_AND_CALLS_TAG,
  leadPageMainTabs,
  LeadPageTab,
} from "@/modules/lead-page/new/constants";
import { PermissionLevel, PermissionsEnum } from "@/modules/common/types";
import { userHasPermission } from "@/modules/common/utils";
import usePassport from "@/modules/common/hooks/passport";
import { useOrganization } from "@/modules/common/context/organizations/get-user-organizations.provider";
import useLeadTracks from "@/modules/lead-page/new/components/developer-tracks/hooks/use-lead-tracks.hook";
import { LeadType } from "@/modules/common/dtos/index.v2";

/** §NawyNowGate: `lead_types.name` rows are stored lowercase — the Nawy Now product lead type. */
const NAWY_NOW_LEAD_TYPE_NAME = "nawy now";

interface UseReviewsTabVisibilityParams {
  activeTab: LeadPageTab;
  setActiveTab: (tab: LeadPageTab) => void;
  leadUid?: string;
  leadType?: Partial<LeadType>;
}

export const useReviewsTabVisibility = ({
  activeTab,
  setActiveTab,
  leadUid,
  leadType,
}: UseReviewsTabVisibilityParams) => {
  const { permissions: currentUserPermissions } = usePassport();

  const isReviewsTabVisible = useMemo(() => {
    // Check permission first (synchronous, instant)
    const hasReviewsTabPermission = userHasPermission(
      {
        name: PermissionsEnum.CAN_VIEW_RATINGS_TAB,
        level: PermissionLevel.READ,
      },
      currentUserPermissions,
    );

    // If user has permission, show tab immediately without waiting for API
    if (hasReviewsTabPermission) {
      return true;
    }

    // If no permission, check API error (only if API call is done)
    return false;
  }, [currentUserPermissions]);

  const { data: tracksData } = useLeadTracks({
    leadUId: leadUid || "",
    enabled: !!leadUid,
  });

  const hasDeveloperTracks =
    leadType?.has_developer_tracks ?? leadType?.hasDeveloperTracks ?? false;
  const hasExistingTracks = (tracksData?.results?.length ?? 0) > 0;

  const isDeveloperTracksTabVisible = useMemo(() => {
    const hasDeveloperTracksTabPermission = userHasPermission(
      {
        name: PermissionsEnum.DEVELOPER_TRACKS_TAB,
        level: PermissionLevel.READ,
      },
      currentUserPermissions,
    );

    return (
      hasDeveloperTracksTabPermission &&
      (hasDeveloperTracks || hasExistingTracks)
    );
  }, [currentUserPermissions, hasDeveloperTracks, hasExistingTracks]);

  // §NawyNowOpen: the launch-cohort permission gate (`nawy_now_tab`) is retired — Nawy Now is
  // open to every CRM user (product ask, 2026-08-15). The LEAD TYPE gate stays: the tab belongs
  // ONLY to "nawy now" leads; it used to render on every lead, letting agents open workflow cases
  // on primary/resale/commercial leads (live bite, 2026-08-04; crm-backend also rejects those
  // server-side). While the lead is still loading (leadType undefined) the tab stays hidden.
  const isNawyNowTabVisible =
    leadType?.name?.toLowerCase() === NAWY_NOW_LEAD_TYPE_NAME;
  // Chats & Calls is tag-gated: the user must carry the tag on their profile.
  const { userTags } = useOrganization();
  const isChatsAndCallsTabVisible = useMemo(
    () => (userTags ?? []).some((tag) => tag.name === CHATS_AND_CALLS_TAG),
    [userTags],
  );

  // Filter tabs based on visibility rules
  const filteredTabs = useMemo(() => {
    return leadPageMainTabs.filter((tab) => {
      if (tab.id === LeadPageTab.REVIEWS && !isReviewsTabVisible) {
        return false;
      }
      if (
        tab.id === LeadPageTab.DEVELOPER_TRACKS &&
        !isDeveloperTracksTabVisible
      ) {
        return false;
      }
      if (tab.id === LeadPageTab.NAWY_NOW && !isNawyNowTabVisible) {
        return false;
      }
      if (
        tab.id === LeadPageTab.CHATS_AND_CALLS &&
        !isChatsAndCallsTabVisible
      ) {
        return false;
      }
      return true;
    });
  }, [
    isReviewsTabVisible,
    isDeveloperTracksTabVisible,
    isNawyNowTabVisible,
    isChatsAndCallsTabVisible,
  ]);

  // If a hidden tab is currently active, switch to Activity tab
  useEffect(() => {
    if (activeTab === LeadPageTab.REVIEWS && !isReviewsTabVisible) {
      setActiveTab(LeadPageTab.ACTIVITY);
    }
    if (
      activeTab === LeadPageTab.DEVELOPER_TRACKS &&
      !isDeveloperTracksTabVisible
    ) {
      setActiveTab(LeadPageTab.ACTIVITY);
    }
    // Deep-link protection (?tab=nawy_now) — lead-type only now: the kick waits until the type
    // is KNOWN, since kicking during load would bounce legitimate nawy-now deep links to Activity.
    if (
      activeTab === LeadPageTab.NAWY_NOW &&
      leadType?.name &&
      !isNawyNowTabVisible
    ) {
      setActiveTab(LeadPageTab.ACTIVITY);
    }
    if (
      activeTab === LeadPageTab.CHATS_AND_CALLS &&
      !isChatsAndCallsTabVisible
    ) {
      setActiveTab(LeadPageTab.ACTIVITY);
    }
  }, [
    activeTab,
    isReviewsTabVisible,
    isDeveloperTracksTabVisible,
    isNawyNowTabVisible,
    leadType?.name,
    isChatsAndCallsTabVisible,
    setActiveTab,
  ]);

  return {
    isReviewsTabVisible,
    isDeveloperTracksTabVisible,
    isNawyNowTabVisible,
    isChatsAndCallsTabVisible,
    filteredTabs,
  };
};
